// Builds the hand-end "add opponent to friends" candidate list from the
// online room roster — split out of `GameScene.ts` for the 200-line cap.

import type { RoomClient } from '@bn-games/room-client';
import type { SeatedPlayer } from '@bn-games/network-protocol';
import type { OpponentIdentity } from '@bn-games/ui-shell';
import type { ClientMessage, ServerMessage } from '../network/protocol.js';
import type { Seat } from '../game/types.js';

/** `null` (not just missing `userId`) means "anonymous, not addable." */
function toOpponentIdentity(player: SeatedPlayer): OpponentIdentity | null {
  if (player.userId === undefined) return null;
  return { userId: player.userId, name: player.name, avatarUrl: player.avatarUrl ?? null };
}

/**
 * Every other human seated at the table this hand — `[]` when `room` is
 * `null` (solo/lesson play), or when every other seat is anonymous.
 * Server-driven bot seats never appear in `room.players` (see
 * `packages/room-server/CLAUDE.md`'s "The room seats only its humans"), so no
 * bot-filtering is needed beyond the `userId` presence check every other
 * game already applies.
 */
export function addOpponentCandidates(
  room: RoomClient<ClientMessage, ServerMessage> | null,
  localSeat: Seat,
): readonly OpponentIdentity[] {
  if (room === null) return [];
  const others: OpponentIdentity[] = [];
  for (const player of room.getInfo().players ?? []) {
    if (player.seat === localSeat) continue;
    const identity = toOpponentIdentity(player);
    if (identity !== null) others.push(identity);
  }
  return others;
}
